import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { Button } from "@/marketing/components/ui/button";
import { Input } from "@/marketing/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import { communityAdminAPIClient } from "@/marketing/infrastructure/influencer/community/communityAdminAPIClient";
import { COMMUNITY_KEYS } from "@/marketing/infrastructure/influencer/community/utils/keys";

interface TrendingManualDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Pin a content/post URL into the Trending ranking (stays above the momentum batch).
export function TrendingManualDialog({ open, onOpenChange }: TrendingManualDialogProps) {
  const queryClient = useQueryClient();
  const [contentUrl, setContentUrl] = useState("");
  const [platform, setPlatform] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");

  useEffect(() => {
    if (!open) return;
    setContentUrl("");
    setPlatform("");
    setThumbnailUrl("");
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      communityAdminAPIClient.createManualTrending({
        contentUrl: contentUrl.trim(),
        ...(platform.trim() !== "" && { platform: platform.trim() }),
        ...(thumbnailUrl.trim() !== "" && { thumbnailUrl: thumbnailUrl.trim() }),
      }),
    onSuccess: () => {
      toast.success("Pinned to Trending");
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.trending() });
      onOpenChange(false);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? "Failed to pin");
    },
  });

  const canSubmit = /^https?:\/\//i.test(contentUrl.trim()) && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-max-w-lg">
        <DialogHeader>
          <DialogTitle>Add to Trending</DialogTitle>
        </DialogHeader>

        <div className="tw-flex tw-flex-col tw-gap-4 tw-px-1">
          <div className="tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-xs tw-text-amber-700">
            Manually feature a post URL — it stays pinned above the momentum ranking and
            survives the batch recompute.
          </div>

          <div>
            <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">
              Post URL <span className="tw-text-red-600">*</span>
            </label>
            <Input
              value={contentUrl}
              onChange={(e) => setContentUrl(e.target.value)}
              placeholder="https://www.tiktok.com/@creator/video/123"
            />
          </div>

          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <div>
              <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">Platform</label>
              <Input
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                placeholder="TikTok"
              />
            </div>
            <div>
              <label className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-text-gray-700">Thumbnail URL</label>
              <Input
                value={thumbnailUrl}
                onChange={(e) => setThumbnailUrl(e.target.value)}
                placeholder="https://…/thumb.jpg"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!canSubmit}>
            {mutation.isPending ? "Pinning…" : "Add to Trending"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
